/** @module models */
import { DateTime } from "luxon";
import React from "react";
import { Model } from "typedefs";
import { CalendarPreferences, DEFAULT_CALENDAR_PREFERENCES } from "app/models/CalendarPreferences";

const DATE_TOKEN_WITH_YEAR: Record<CalendarPreferences["dateFormat"], string> = {
  us: "MMM d, yyyy",
  eu: "d MMM, yyyy",
};

const TIME_TOKEN: Record<CalendarPreferences["timeFormat"], string> = {
  "12h": "h:mm a",
  "24h": "HH:mm",
};

export class BookingLogModel implements Model {
  coachName?: string;
  coachPicture?: string;
  coachProfileId?: number;
  createdAt?: string;
  creator?: {
    firstName?: string;
    id?: number;
    lastName?: string;
    type?: string;
  };
  id?: number;
  info?: {
    lessonScheduleId?: string;
    messagePlaceholdersForCoach?: {
      date?: string;
      endTime?: string;
      startTime?: string;
      studentNames?: string;
      studentName?: string;
    };
    messagePlaceholdersForStudent?: {
      coachName?: string;
      date?: string;
      startTime?: string;
    };
  };
  logType?: string;
  messageForCoach?: string;
  messageForStudent?: string;
  participantPicture?: string;
  participantsNames?: string[];
  timeZone?: string;
  isCoach?: boolean;
  preferences: CalendarPreferences;

  constructor(bookingLog: any, isCoach: boolean, preferences: CalendarPreferences = DEFAULT_CALENDAR_PREFERENCES) {
    Object.assign(this, structuredClone(bookingLog));
    this.isCoach = isCoach;
    this.preferences = preferences;
  }

  public highlightMessageParts = (): JSX.Element[] => {
    const fragments: JSX.Element[] = [];
    let remaining = this.isCoach ? this.messageForCoach : this.messageForStudent;
    const partsToBold = this.getBookingLogPlaceholders();

    partsToBold.forEach((part, index) => {
      if (typeof part !== "string" || !part.trim()) {
        return;
      }

      const splitIndex = remaining.indexOf(part);

      if (splitIndex === -1) {
        return;
      }

      const before = remaining.slice(0, splitIndex);
      const after = remaining.slice(splitIndex + part.length);

      if (before) {
        fragments.push(<span key={`before-${index}`}>{before}</span>);
      }

      fragments.push(
        <span key={`bold-${index}`} className="bold">
          {part}
        </span>
      );

      remaining = after;
    });

    if (remaining) {
      fragments.push(<span key="last">{remaining}</span>);
    }

    return fragments;
  }

  private getBookingLogPlaceholders = () => {
    return this.isCoach
      ? [
          this.info?.messagePlaceholdersForCoach?.studentNames || this.info?.messagePlaceholdersForCoach?.studentName,
          this.info?.messagePlaceholdersForCoach?.date,
          this.info?.messagePlaceholdersForCoach?.startTime,
          this.info?.messagePlaceholdersForCoach?.endTime,
        ]
      : [
          this.info?.messagePlaceholdersForStudent?.coachName,
          this.info?.messagePlaceholdersForStudent?.date,
          this.info?.messagePlaceholdersForStudent?.startTime,
        ];
  }

  public getPictureUrl = () => {
    return this.isCoach ? this.participantPicture : this.coachPicture;
  }

  public getFirstUserName = () => {
    return this.isCoach ? this.participantsNames[0] : this.coachName;
  }

  public getUsersNames = () => {
    return this.isCoach ? this.participantsNames.join(', ') : this.coachName;
  }

  public formatFullDateWithTimeZone = (): string | null => {
    if (!this.createdAt) {
      return null;
    }

    const dateTime = DateTime.fromISO(this.createdAt, { zone: this.timeZone });
    const dateToken = DATE_TOKEN_WITH_YEAR[this.preferences.dateFormat];
    const timeToken = TIME_TOKEN[this.preferences.timeFormat];

    return `${dateTime.toFormat(`${dateToken} - ${timeToken}`)} ${dateTime.offsetNameShort}`;
  }

  public getIcoType = (): string => {
    switch (this.logType) {
      case 'create':
        return "ico ico-calendar";
      case 'update':
        return "ico ico-repeat";
      case 'delete':
        return "ico ico-calendar-x";
      default:
        return "ico ico-calendar";
    }
  }
}
